import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuideHintTooltip } from './guide/GuideHintTooltip';
import { actorRoleSection, GuideTarget, nodeFlagSection } from './guide/guideContent';
import { useSocialGraphTrack } from './analytics';
import { LayoutMode } from './layoutPipeline';
import {
  ActiveGraphKind,
  ActorGroupMember,
  ACTOR_ROLE_DESCRIPTIONS,
  ANALYSIS_MODE_DESCRIPTIONS,
  AnalysisMode,
  CommunityClusterItem,
  FlagGroup,
  GraphColorMode,
  GraphHighlight,
  LAYOUT_MODE_DESCRIPTIONS,
  NODE_FLAG_DESCRIPTIONS,
  NODE_FLAG_LABELS,
  MIN_AVG_ZSCORE_STEPS,
  NodeFilterCategory,
  NodeFilters,
  NodeSizeMetric,
  PanelTab,
  PANEL_TABS,
  PANEL_TAB_LABELS,
  RoleGroup,
  SIZE_METRIC_DESCRIPTIONS,
  SIZE_METRIC_LABELS,
  HIDDEN_ACTOR_ROLE_CATEGORIES,
  VISIBLE_LAYOUT_MODES,
  VISIBLE_SIZE_METRICS,
  ZSCORE_ENABLED,
  ZSCORE_BUCKET_DESCRIPTIONS,
  ZSCORE_BUCKET_LABELS,
  ZScoreBucket,
  ZScoreGroup,
} from './graphTypes';
import { COMMUNITY_PALETTE, InterClusterMode } from './graphColors';
import { mentionTypeLabel } from './timelineUtils';
import { NodeFilterSection } from './filterSections';
import { PanelSelect } from './PanelSelect';
import { useMenuDismiss } from './useMenuDismiss';
import { GraphHostActions } from './hostActions';
import { ReactComponent as SettingsIcon } from './assets/settings.svg';
import { ReactComponent as FiltersIcon } from './assets/filters.svg';
import { ReactComponent as ActorsIcon } from './assets/actors.svg';
import { ReactComponent as SearchIcon } from './assets/search.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as ChevronIcon } from './assets/chevron.svg';
import { ReactComponent as CheckBoxIcon } from './assets/check-box.svg';
import { ReactComponent as KebabIcon } from './assets/kebab.svg';
import { ReactComponent as HintIcon } from './assets/hint.svg';
import { ReactComponent as AddToGroupIcon } from './assets/add-to-group-sm.svg';
import styles from './SocialGraphPOC.module.scss';

// tooltip anchor for the ⓘ hints on Actors-tab group rows
const GROUP_HINT_TOOLTIP_ID = 'socialGraphGroupHint';

// Section-title hint copy — each string renders twice (visual tooltip + sr-only
// text), so keep the single source here instead of inline literals.
const NODE_SIZE_METRIC_HINT =
  'Shows how important an actor is. Each metric answers that question differently — pick the one that matches what you’re looking for.';
const COMMUNITIES_HINT =
  'Groups of actors that interact with each other far more than with the rest of the network. Detected automatically, not labelled by hand.';
const THICKNESS_HINT =
  'Thicker connection = more interactions between two actors. When off, all connections stay thin; thickness still applies to connections of the selected actor.';
const HIGHLIGHT_FILTER_HINT = 'Keep filtered-out actors visible, but dimmed.';
const PERFORMANCE_MODE_HINT =
  'Straight connections, and labels are hidden while you pan or zoom. Faster on very large graphs; curves come back when off.';
const ANALYSIS_MODE_HINT =
  'Which engine built this graph. Behavioural metrics and the anomaly edge colours exist only in Z-Score mode.';
const COLOR_SCHEME_HINT =
  'What the node and connection colours stand for — community, interaction type, actor group, internal / external connection or platform.';
// guide chapter behind each Node size metric option hint
const SIZE_METRIC_GUIDE: Record<NodeSizeMetric, GuideTarget> = {
  pagerank: { page: 'configuration', section: 'pagerank' },
  // no chapter of their own — the Node size metric section explains the pair the guide documents
  betweenness: { page: 'configuration', section: 'size-metric' },
  hub_score: { page: 'configuration', section: 'size-metric' },
  authority_score: { page: 'configuration', section: 'size-metric' },
  degree: { page: 'configuration', section: 'degree' },
  anomaly: { page: 'limitations' },
};
const INTER_CLUSTER_HINT =
  'How connections between different communities are drawn: dimmed, hidden, or at full strength like the rest.';

const TAB_ICONS: Record<PanelTab, React.FC<React.SVGProps<SVGSVGElement>>> = {
  configuration: SettingsIcon,
  filters: FiltersIcon,
  actors: ActorsIcon,
  search: SearchIcon,
};

export interface SearchMatch {
  id: string;
  label: string;
  community: number;
}

interface GraphPanelProps {
  activeGraphKind: ActiveGraphKind;
  // single-actor explore graphs are exploratory-only (their zscore payload is
  // an empty shell) — hide the Analysis mode toggle for them
  isExploreGraph?: boolean;
  mode: AnalysisMode;
  handleModeChange: (mode: AnalysisMode) => void;
  sizeMetric: NodeSizeMetric;
  setSizeMetric: (metric: NodeSizeMetric) => void;
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  colorMode: GraphColorMode;
  setColorMode: (mode: GraphColorMode) => void;
  interClusterMode: InterClusterMode;
  setInterClusterMode: (mode: InterClusterMode) => void;
  mentionTypes: { type: string; count: number }[];
  // checked = only those interaction types stay (empty = all)
  selectedMentionTypes: string[];
  onToggleMentionType: (type: string) => void;
  // node facet filters (Filters tab → Actors): sections present in the graph
  // with actor counts (built on the page, shared with the canvas chips);
  // checking values narrows the canvas to matching actors
  nodeFilters: NodeFilters;
  nodeFilterSections: NodeFilterSection[];
  onToggleNodeFilter: (category: NodeFilterCategory, value: string) => void;
  // "Highlight instead of hide": facet-filtered actors stay on the canvas dimmed
  dimFilteredNodes: boolean;
  onDimFilteredNodesChange: (value: boolean) => void;
  // edge-side filters beyond relation types: z-score anomaly buckets
  // (uncheck-to-hide, z-score coloring only) and the mutual-only switch
  edgeAnomalyOptions: { bucket: ZScoreBucket; count: number }[];
  hiddenEdgeAnomalyBuckets: ZScoreBucket[];
  onToggleEdgeAnomalyBucket: (bucket: ZScoreBucket) => void;
  mutualOnly: boolean;
  onMutualOnlyChange: (value: boolean) => void;
  reciprocalCount: number;
  // "Minimum connections" number input: clamped to the graph's max degree
  // (0 = no edges → hidden); the helper line counts the actors still matching
  onMinDegreeChange: (value: number) => void;
  maxDegree: number;
  visibleActorCount: number;
  onMinAvgZScoreChange: (value: number | null) => void;
  showEdgeArrows: boolean;
  setShowEdgeArrows: (value: boolean) => void;
  volumeSizedEdges: boolean;
  setVolumeSizedEdges: (value: boolean) => void;
  showKeyConnectors: boolean;
  setShowKeyConnectors: (value: boolean) => void;
  showTimeline: boolean;
  onShowTimelineChange: (value: boolean) => void;
  // Performance mode exists only in the social_graph_v2 implementation; the
  // legacy page passes neither prop and the switch stays out of the panel
  performanceMode?: boolean;
  setPerformanceMode?: (value: boolean) => void;
  zscoreEdgeColoring: boolean;
  communityClusters: CommunityClusterItem[];
  communityMembers: Map<number, ActorGroupMember[]>;
  roleGroups: RoleGroup[];
  zscoreGroups: ZScoreGroup[];
  flagGroups: FlagGroup[];
  // actors the facet filters removed from the canvas (hide mode only, else null)
  hiddenNodeIds: Set<string> | null;
  graphHighlight: GraphHighlight | null;
  setGraphHighlight: (highlight: GraphHighlight | null) => void;
  setFocusedNodeId: (id: string | null) => void;
  actions: GraphHostActions | null;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  searchMatches: SearchMatch[];
  searchLoading: boolean;
  onClearSearch: () => void;
  // lets the page shift canvas overlays (stats chips, legend) out from under
  // the opened panel
  onOpenChange?: (open: boolean) => void;
}

// a11y-hidden native checkbox + styled box (Figma node 272-95); the row itself
// is the label so the whole line is clickable
const CheckboxRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <label className={styles.panelSettingsRow}>
    <span>{label}</span>
    <input type="checkbox" className={styles.srOnly} checked={checked} onChange={onChange} />
    <span className={styles.panelCheckbox} aria-hidden="true">
      <CheckBoxIcon />
    </span>
  </label>
);

// whole-row toggle button with a decorative switch (Figma nodes 272-82 / 272-123)
const SwitchRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  // optional ⓘ explainer: hover/focus tooltip on the row, exposed to screen
  // readers as the switch's description (NOT its name — the sr-only text lives
  // outside the button so it doesn't bloat the accessible name)
  hint?: string;
  // guide chapter the hint bubble's "Learn more" opens
  guide?: GuideTarget;
}> = ({ label, checked, onChange, hint, guide }) => {
  const hintId = useId();
  return (
    <>
      {/* rendered BEFORE the button so the row buttons stay each other's
          siblings and the group's :last-child border rule keeps working */}
      {hint && (
        <span id={hintId} className={styles.srOnly}>
          {hint}
        </span>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={hint ? hintId : undefined}
        className={styles.panelSettingsRow}
        onClick={() => onChange(!checked)}
        data-tooltip-id={hint ? GROUP_HINT_TOOLTIP_ID : undefined}
        data-tooltip-content={hint}
        data-guide-page={hint ? guide?.page : undefined}
        data-guide-section={hint ? guide?.section : undefined}
      >
        <span className={styles.panelSettingsLabel}>
          {label}
          {hint && <HintIcon className={styles.actorGroupHint} aria-hidden="true" />}
        </span>
        <span className={styles.panelSwitch} aria-hidden="true" />
      </button>
    </>
  );
};

// Pruning slider over discrete thresholds (position 0 = off) — network metrics
// are heavy-tailed, so the steps give a log-ish sweep instead of a wasted linear
// track. Native range input for keyboard/SR support; aria-valuetext announces
// the actual threshold.
const FilterSlider: React.FC<{
  id: string;
  label: string;
  value: number | null; // active threshold, null = off
  thresholds: number[];
  onChange: (threshold: number | null) => void;
  // display for an active value; default suits "at least N" thresholds
  format?: (value: number) => string;
}> = ({ id, label, value, thresholds, onChange, format }) => {
  const { t } = useTranslation();
  // indexOf with a top-clamp fallback: a mode switch can shrink the threshold
  // list under an active value — the knob then sticks to the top step instead of
  // silently jumping to Off while the filter still applies
  const index = value !== null ? thresholds.indexOf(value) : -1;
  const position = value === null ? 0 : index >= 0 ? index + 1 : thresholds.length;
  const display = value !== null ? (format ? format(value) : `≥ ${value}`) : t('Off');
  return (
    <div className={styles.nodePanelSection}>
      <div className={styles.panelSliderHead}>
        <label htmlFor={id} className={styles.nodePanelSectionTitle}>
          {label}
        </label>
        <span className={styles.panelSliderValue}>{display}</span>
      </div>
      <input
        id={id}
        className={styles.panelSlider}
        type="range"
        min={0}
        max={thresholds.length}
        step={1}
        value={position}
        aria-valuetext={display}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(next === 0 ? null : thresholds[next - 1]);
        }}
      />
    </div>
  );
};

// Actors | Connections tabs of the Filters tab (Figma 1229-68): the two filter
// families are split so neither list buries the other. Real tabs — roving
// tabindex, arrow keys / Home / End move the selection.
type FiltersSubTab = 'actors' | 'connections';
const FILTER_SUB_TABS: { id: FiltersSubTab; label: string }[] = [
  { id: 'actors', label: 'Actors' },
  { id: 'connections', label: 'Connections' },
];
const FILTERS_TAB_ID = 'socialGraphFilters';

const FilterTabs: React.FC<{
  active: FiltersSubTab;
  onChange: (tab: FiltersSubTab) => void;
}> = ({ active, onChange }) => {
  const { t } = useTranslation();
  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = FILTER_SUB_TABS.length - 1;
    const jumps: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: last - index };
    const delta = jumps[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    const next = FILTER_SUB_TABS[(index + delta + FILTER_SUB_TABS.length) % FILTER_SUB_TABS.length].id;
    onChange(next);
    document.getElementById(`${FILTERS_TAB_ID}-tab-${next}`)?.focus();
  };
  return (
    <div className={`${styles.segmentedControl} ${styles.filterTabs}`} role="tablist" aria-label={t('Filter groups')}>
      {FILTER_SUB_TABS.map(({ id, label }, index) => (
        <button
          key={id}
          type="button"
          role="tab"
          id={`${FILTERS_TAB_ID}-tab-${id}`}
          aria-selected={active === id}
          aria-controls={`${FILTERS_TAB_ID}-panel-${id}`}
          tabIndex={active === id ? 0 : -1}
          className={`${styles.segmentedTab} ${styles.filterTab} ${active === id ? styles.segmentedTabActive : ''}`}
          onClick={() => onChange(id)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {t(label)}
        </button>
      ))}
    </div>
  );
};

// A facet section of the Actors sub-tab: title + checkbox card. Sections with
// more than FACET_OPTIONS_SHOWN values fold the rest behind "Show N more"
// (Figma 1229-98) — the biggest lists (communities, countries) run to hundreds.
const FACET_OPTIONS_SHOWN = 10;

const FacetSection: React.FC<{
  section: NodeFilterSection;
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ section, selected, onToggle }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const foldedCount = section.options.length - FACET_OPTIONS_SHOWN;
  const options = expanded || foldedCount <= 0 ? section.options : section.options.slice(0, FACET_OPTIONS_SHOWN);
  return (
    <div className={styles.nodePanelSection}>
      <span className={styles.nodePanelSectionTitle}>{t(section.title)}</span>
      <div className={styles.panelSettingsGroup}>
        {options.map(({ value, label, count }) => (
          <CheckboxRow
            key={value}
            label={`${label} (${count})`}
            checked={selected.includes(value)}
            onChange={() => onToggle(value)}
          />
        ))}
        {foldedCount > 0 && (
          <button
            type="button"
            className={styles.panelShowMore}
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? t('Show less') : t('Show {{count}} more', { count: foldedCount })}
            <ChevronIcon className={expanded ? styles.panelShowMoreChevronUp : undefined} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

// Integer threshold input (Figma 1229-89): empty = no filter ("Any"), digits
// only, clamped to `max`; the × clears, the helper line counts the actors that
// still match while typing. The draft string is local so a cleared field doesn't
// snap back to "0".
const FilterNumberInput: React.FC<{
  id: string;
  label: string;
  value: number; // committed threshold, 0 = off
  max: number;
  matchCount: number;
  onChange: (value: number) => void;
}> = ({ id, label, value, max, matchCount, onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value > 0 ? String(value) : '');
  // external resets (chip ×, new dataset) overwrite the draft; typing never
  // round-trips here because the committed value always mirrors the draft
  useEffect(() => {
    setDraft((current) => (Number(current || 0) === value ? current : value > 0 ? String(value) : ''));
  }, [value]);
  const commit = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    const next = digits === '' ? 0 : Math.min(Number(digits), max);
    setDraft(next > 0 ? String(next) : '');
    onChange(next);
  };
  const helperId = `${id}-help`;
  return (
    <div className={styles.nodePanelSection}>
      <label htmlFor={id} className={styles.nodePanelSectionTitle}>
        {label}
      </label>
      <div className={styles.panelNumberField}>
        <input
          id={id}
          className={styles.panelNumberInput}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder={t('Any')}
          value={draft}
          onChange={(event) => commit(event.target.value)}
          aria-describedby={value > 0 ? helperId : undefined}
        />
        {value > 0 && (
          <button type="button" className={styles.panelNumberClear} onClick={() => commit('')} aria-label={t('Clear')}>
            <CloseIcon aria-hidden="true" />
          </button>
        )}
      </div>
      {value > 0 && (
        <p id={helperId} className={styles.panelNumberHelp}>
          {t('{{count}} actors match', { count: matchCount })}
        </p>
      )}
    </div>
  );
};

// Kebab dropdown on an Actors-tab group row (Figma node 352-62): "Add to group".
// "See actors" was dropped on PM request (2026-10-16) — clicking the row opens
// the group on the right with its full member list, so the in-panel drill-down
// it led to is gone too. Same WAI-ARIA menu-button pattern as GraphHeader's
// Export menu — Escape consumes the event (preventDefault) so it never closes
// an enclosing graph overlay in the same keypress.
const RowKebabMenu: React.FC<{
  name: string;
  onAddAll: () => void;
}> = ({ name, onAddAll }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useMenuDismiss(open, wrapRef, triggerRef, () => setOpen(false));

  const select = (handler: () => void) => {
    setOpen(false);
    triggerRef.current?.focus();
    handler();
  };

  const focusMenuItem = (fromIndex: number, direction: 1 | -1) => {
    const items = wrapRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    items[(fromIndex + direction + items.length) % items.length]?.focus();
  };

  const menuItemKeys = (index: number) => (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusMenuItem(index, 1);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusMenuItem(index, -1);
    }
  };

  return (
    <div className={styles.actorGroupMenuWrap} ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.actorGroupMenuBtn}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Actions for {{name}}', { name })}
        onClick={() => setOpen((current) => !current)}
      >
        <KebabIcon aria-hidden="true" />
      </button>
      {open && (
        <div className={styles.actorGroupMenu} role="menu" aria-label={t('Actions for {{name}}', { name })}>
          <button
            type="button"
            role="menuitem"
            className={styles.actorGroupMenuItem}
            onClick={() => select(onAddAll)}
            onKeyDown={menuItemKeys(0)}
          >
            <AddToGroupIcon aria-hidden="true" />
            {t('Add to group')}
          </button>
        </div>
      )}
    </div>
  );
};

// Left rail + slide-out panels (Configuration / Filters / Actors / Search),
// Figma nodes 280-1640, 288-279, 289-317, 289-345. Rail is always visible;
// clicking the open tab again (or the ✕) closes the panel. The panel overlays
// the canvas so opening it never resizes the WebGL stage.
export const GraphPanel: React.FC<GraphPanelProps> = ({
  activeGraphKind,
  isExploreGraph = false,
  mode,
  handleModeChange,
  sizeMetric,
  setSizeMetric,
  layoutMode,
  setLayoutMode,
  colorMode,
  setColorMode,
  interClusterMode,
  setInterClusterMode,
  mentionTypes,
  selectedMentionTypes,
  onToggleMentionType,
  nodeFilters,
  nodeFilterSections,
  onToggleNodeFilter,
  dimFilteredNodes,
  onDimFilteredNodesChange,
  edgeAnomalyOptions,
  hiddenEdgeAnomalyBuckets,
  onToggleEdgeAnomalyBucket,
  mutualOnly,
  onMutualOnlyChange,
  reciprocalCount,
  onMinDegreeChange,
  maxDegree,
  visibleActorCount,
  onMinAvgZScoreChange,
  showEdgeArrows,
  setShowEdgeArrows,
  volumeSizedEdges,
  setVolumeSizedEdges,
  showKeyConnectors,
  setShowKeyConnectors,
  showTimeline,
  onShowTimelineChange,
  performanceMode,
  setPerformanceMode,
  zscoreEdgeColoring,
  communityClusters,
  communityMembers,
  roleGroups,
  zscoreGroups,
  flagGroups,
  hiddenNodeIds,
  graphHighlight,
  setGraphHighlight,
  setFocusedNodeId,
  actions,
  searchQuery,
  onSearchQueryChange,
  searchMatches,
  searchLoading,
  onClearSearch,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const track = useSocialGraphTrack();
  const [openTab, setOpenTab] = useState<PanelTab | null>(null);
  const [filtersTab, setFiltersTab] = useState<FiltersSubTab>('actors');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const panelTitleRef = useRef<HTMLHeadingElement>(null);

  // focus the search input as soon as its panel opens
  useEffect(() => {
    if (openTab === 'search') searchInputRef.current?.focus();
  }, [openTab]);

  // mirror the open state up; reset on unmount so overlays slide back
  useEffect(() => {
    onOpenChange?.(openTab !== null);
    return () => onOpenChange?.(false);
  }, [openTab, onOpenChange]);

  // Category rows hidden on UI only (PM decision 2026-09-17): the groups stay
  // in the page state for spotlights and drills; Regular node is hidden on
  // every graph, Critical bridges on case graphs (no bridge semantics there)
  const visibleRoleGroups = useMemo(
    () => roleGroups.filter(({ role }) => !HIDDEN_ACTOR_ROLE_CATEGORIES.includes(role)),
    [roleGroups],
  );
  const visibleFlagGroups = useMemo(
    () => flagGroups.filter(({ flag }) => !(activeGraphKind === 'case' && flag === 'connector')),
    [flagGroups, activeGraphKind],
  );

  // The Actors tab mirrors the canvas: in hide mode its rows, counts and drill
  // lists cover only the actors still drawn (a hidden actor can't be focused —
  // the page unpins it at once). The facet checkboxes keep the full groups on
  // purpose: their counts describe the whole graph, and narrowing them by the
  // active filter would make the other options disappear.
  const onCanvas = useCallback(
    <M extends { id: string }>(members: M[]): M[] =>
      hiddenNodeIds ? members.filter((member) => !hiddenNodeIds.has(member.id)) : members,
    [hiddenNodeIds],
  );
  const actorsTabRoleGroups = useMemo(
    () =>
      visibleRoleGroups
        .map((group) => ({ ...group, members: onCanvas(group.members) }))
        .filter((group) => group.members.length > 0),
    [visibleRoleGroups, onCanvas],
  );
  const actorsTabFlagGroups = useMemo(
    () =>
      visibleFlagGroups
        .map((group) => ({ ...group, members: onCanvas(group.members) }))
        .filter((group) => group.members.length > 0),
    [visibleFlagGroups, onCanvas],
  );
  const actorsTabZscoreGroups = useMemo(
    () =>
      zscoreGroups
        .map((group) => ({ ...group, members: onCanvas(group.members) }))
        .filter((group) => group.members.length > 0),
    [zscoreGroups, onCanvas],
  );
  const actorsTabCommunities = useMemo(
    () =>
      communityClusters
        .map((cluster) => ({ cluster, members: onCanvas(communityMembers.get(cluster.id) ?? []) }))
        .filter(({ members }) => members.length > 0),
    [communityClusters, communityMembers, onCanvas],
  );

  const toggleTab = (tab: PanelTab) => {
    if (openTab !== tab) track('sg_panel_tab_opened', { tab });
    setOpenTab((current) => (current === tab ? null : tab));
  };

  const showMinZScoreSlider = zscoreGroups.length > 0;
  const hasActorFilters = nodeFilterSections.length > 0 || showMinZScoreSlider;
  const hasConnectionFilters =
    mentionTypes.length > 1 || edgeAnomalyOptions.length > 1 || reciprocalCount > 0 || maxDegree > 0;
  const hasFilterUi = hasActorFilters || hasConnectionFilters;

  // One row of an Actors-tab group box (Figma node 352-463): the main button
  // toggles the spotlight, the kebab opens the row menu (add to group / drill
  // into the full member list). The ⓘ description shows as a hover/focus tooltip
  // on the row and is repeated as screen-reader-only text inside the button.
  // Rows are deliberately dot-less (designer removed all group color coding).
  const renderGroupRow = (opts: {
    key: string;
    title: string;
    subtitle: string;
    description?: string;
    active: boolean;
    onToggle: () => void;
    onAddAll?: () => void;
    // z-score titles can wrap to two lines — let the hint icon trail the text
    inlineHint?: boolean;
    // guide chapter the hint bubble's "Learn more" opens (see GuideHintTooltip)
    guide?: GuideTarget;
  }) => (
    <div key={opts.key} className={styles.actorGroupRow}>
      <button
        type="button"
        className={styles.actorGroupRowMain}
        aria-pressed={opts.active}
        onClick={opts.onToggle}
        data-tooltip-id={opts.description ? GROUP_HINT_TOOLTIP_ID : undefined}
        data-tooltip-content={opts.description}
        data-guide-page={opts.description ? opts.guide?.page : undefined}
        data-guide-section={opts.description ? opts.guide?.section : undefined}
      >
        <span className={styles.actorGroupText}>
          <span
            className={`${styles.actorGroupTitle}${opts.inlineHint ? ` ${styles.actorGroupTitleInline}` : ''}`}
          >
            {opts.title}
            {opts.description && <HintIcon className={styles.actorGroupHint} aria-hidden="true" />}
          </span>
          {opts.description && <span className={styles.srOnly}>{opts.description}</span>}
          <span className={styles.actorGroupSub}>{opts.subtitle}</span>
        </span>
      </button>
      {/* the menu's only action needs host actions — no kebab without them */}
      {opts.onAddAll && <RowKebabMenu name={opts.title} onAddAll={opts.onAddAll} />}
    </div>
  );

  return (
    <>
      <div className={styles.leftRail}>
        {PANEL_TABS.map((tab) => {
          const Icon = TAB_ICONS[tab];
          return (
            <button
              key={tab}
              type="button"
              className={styles.leftRailBtn}
              aria-pressed={openTab === tab}
              aria-controls="socialGraphLeftPanel"
              aria-label={t(PANEL_TAB_LABELS[tab])}
              title={t(PANEL_TAB_LABELS[tab])}
              onClick={() => toggleTab(tab)}
            >
              <Icon aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {openTab && (
        <aside id="socialGraphLeftPanel" className={styles.leftPanel} aria-label={t(PANEL_TAB_LABELS[openTab])}>
          <div className={styles.nodePanelHeader}>
            <h2 ref={panelTitleRef} tabIndex={-1} className={styles.nodePanelTitle}>
              {t(PANEL_TAB_LABELS[openTab])}
            </h2>
            <button
              type="button"
              className={styles.nodePanelClose}
              onClick={() => setOpenTab(null)}
              aria-label={t('Close panel')}
            >
              <CloseIcon aria-hidden="true" />
            </button>
          </div>
          {openTab === 'configuration' && (
            <div className={styles.nodePanelBody}>
              {activeGraphKind === 'mention' && !isExploreGraph && ZSCORE_ENABLED && (
                <div className={styles.nodePanelSection}>
                  <span
                    className={styles.nodePanelSectionTitleRow}
                    data-tooltip-id={GROUP_HINT_TOOLTIP_ID}
                    data-tooltip-content={t(ANALYSIS_MODE_HINT)}
                    data-guide-page="calculations"
                  >
                    <span id="socialGraphAnalysisModeLabel" className={styles.nodePanelSectionTitle}>
                      {t('Analysis mode')}
                    </span>
                    <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                    <span className={styles.srOnly}>{t(ANALYSIS_MODE_HINT)}</span>
                  </span>
                  <div
                    className={styles.segmentedControl}
                    role="group"
                    aria-labelledby="socialGraphAnalysisModeLabel"
                  >
                    <button
                      type="button"
                      className={`${styles.segmentedTab} ${mode === 'exploratory' ? styles.segmentedTabActive : ''}`}
                      onClick={() => handleModeChange('exploratory')}
                      aria-pressed={mode === 'exploratory'}
                    >
                      {t('Louvain')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.segmentedTab} ${mode === 'zscore' ? styles.segmentedTabActive : ''}`}
                      onClick={() => handleModeChange('zscore')}
                      aria-pressed={mode === 'zscore'}
                    >
                      {t('Z-Score')}
                    </button>
                  </div>
                  <p className={styles.overviewNote}>{t(ANALYSIS_MODE_DESCRIPTIONS[mode])}</p>
                </div>
              )}
              <div className={styles.nodePanelSection}>
                <span
                  className={styles.nodePanelSectionTitleRow}
                  data-tooltip-id={GROUP_HINT_TOOLTIP_ID}
                  data-tooltip-content={t(NODE_SIZE_METRIC_HINT)}
                  data-guide-page="configuration"
                  data-guide-section="size-metric"
                >
                  <label htmlFor="socialGraphSizeMetric" className={styles.nodePanelSectionTitle}>
                    {t('Node size metric')}
                  </label>
                  <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                  <span className={styles.srOnly}>
                    {t(NODE_SIZE_METRIC_HINT)}
                  </span>
                </span>
                <PanelSelect
                  inputId="socialGraphSizeMetric"
                  value={sizeMetric}
                  // 'anomaly' is z-score-derived — offered only while the kill-switch is off
                  options={[...VISIBLE_SIZE_METRICS, ...(ZSCORE_ENABLED ? (['anomaly'] as NodeSizeMetric[]) : [])]
                    .map((key) => ({
                      value: key,
                      label: t(SIZE_METRIC_LABELS[key]),
                      hint: t(SIZE_METRIC_DESCRIPTIONS[key]),
                      guide: SIZE_METRIC_GUIDE[key],
                    }))}
                  onChange={(value) => {
                    setSizeMetric(value as NodeSizeMetric);
                    setFocusedNodeId(null);
                  }}
                />
              </div>
              <div className={styles.nodePanelSection}>
                <label htmlFor="socialGraphLayoutMode" className={styles.nodePanelSectionTitle}>
                  {t('Layout')}
                </label>
                <PanelSelect
                  inputId="socialGraphLayoutMode"
                  value={layoutMode}
                  options={[
                    {
                      value: 'organic',
                      label: t('Organic bridges'),
                      hint: t(LAYOUT_MODE_DESCRIPTIONS.organic),
                      guide: { page: 'configuration', section: 'layout-organic' },
                    },
                    {
                      value: 'packed',
                      label: t('Separated clusters'),
                      hint: t(LAYOUT_MODE_DESCRIPTIONS.packed),
                      guide: { page: 'configuration', section: 'layout-packed' },
                    },
                    {
                      value: 'radial',
                      label: t('Hub-centered (radial)'),
                      hint: t(LAYOUT_MODE_DESCRIPTIONS.radial),
                      guide: { page: 'configuration', section: 'layout' },
                    },
                  ].filter((option) => VISIBLE_LAYOUT_MODES.includes(option.value as LayoutMode))}
                  onChange={(value) => setLayoutMode(value as LayoutMode)}
                />
              </div>
              <div className={styles.nodePanelSection}>
                <span
                  className={styles.nodePanelSectionTitleRow}
                  data-tooltip-id={GROUP_HINT_TOOLTIP_ID}
                  data-tooltip-content={t(COLOR_SCHEME_HINT)}
                  data-guide-page="configuration"
                  data-guide-section="color-scheme"
                >
                  <label htmlFor="socialGraphColorMode" className={styles.nodePanelSectionTitle}>
                    {t('Color scheme')}
                  </label>
                  <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                  <span className={styles.srOnly}>{t(COLOR_SCHEME_HINT)}</span>
                </span>
                <PanelSelect
                  inputId="socialGraphColorMode"
                  value={colorMode}
                  // z-score coloring owns the edges, so the edge-centric schemes
                  // (interaction type, connection internal/external) have nothing
                  // to show there — the page derives the default instead
                  options={[
                    { value: 'community', label: t('By community') },
                    ...(zscoreEdgeColoring ? [] : [{ value: 'mention', label: t('By interaction type') }]),
                    // scope = in / out of the actor group the graph was built from,
                    // connection = flow across that same boundary, by direction —
                    // only actor-group graphs have it (a case graph is all internal,
                    // an explore graph's in-scope flag marks one actor)
                    ...(activeGraphKind === 'mention' && !isExploreGraph
                      ? [
                          { value: 'scope', label: t('By actor group') },
                          ...(zscoreEdgeColoring
                            ? []
                            : [{ value: 'connection', label: t('By connection (internal / external)') }]),
                        ]
                      : []),
                    { value: 'platform', label: t('By platform') },
                    { value: 'mono', label: t('Monochrome') },
                  ]}
                  onChange={(value) => setColorMode(value as GraphColorMode)}
                />
                {zscoreEdgeColoring && (
                  <p className={styles.overviewNote}>
                    {t('In Z-Score mode edge color shows interaction anomaly — see the legend. The scheme above applies to nodes.')}
                  </p>
                )}
              </div>
              <div className={styles.nodePanelSection}>
                <span
                  className={styles.nodePanelSectionTitleRow}
                  data-tooltip-id={GROUP_HINT_TOOLTIP_ID}
                  data-tooltip-content={t(INTER_CLUSTER_HINT)}
                  data-guide-page="configuration"
                  data-guide-section="cross-cluster"
                >
                  <label htmlFor="socialGraphInterCluster" className={styles.nodePanelSectionTitle}>
                    {t('Cross-cluster links')}
                  </label>
                  <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                  <span className={styles.srOnly}>{t(INTER_CLUSTER_HINT)}</span>
                </span>
                <PanelSelect
                  inputId="socialGraphInterCluster"
                  value={interClusterMode}
                  options={[
                    { value: 'dimmed', label: t('Dimmed') },
                    { value: 'hidden', label: t('Hidden') },
                    { value: 'full', label: t('Full') },
                  ]}
                  onChange={(value) => setInterClusterMode(value as InterClusterMode)}
                />
              </div>
              <div className={styles.nodePanelSection}>
                <span className={styles.nodePanelSectionTitle}>{t('Display options')}</span>
                <div className={styles.panelSettingsGroup}>
                  <SwitchRow label={t('Direction arrows')} checked={showEdgeArrows} onChange={setShowEdgeArrows} />
                  <SwitchRow
                    label={t('Thickness by interactions')}
                    checked={volumeSizedEdges}
                    onChange={setVolumeSizedEdges}
                    hint={t(THICKNESS_HINT)}
                    guide={{ page: 'configuration', section: 'thickness' }}
                  />
                  {/* case graphs have no bridge semantics — same gate as the Actors-tab group */}
                  {activeGraphKind !== 'case' && (
                    <SwitchRow
                      label={t('Highlight critical bridges')}
                      checked={showKeyConnectors}
                      onChange={setShowKeyConnectors}
                      hint={t(NODE_FLAG_DESCRIPTIONS.connector)}
                    />
                  )}
                  <SwitchRow label={t('Activity timeline')} checked={showTimeline} onChange={onShowTimelineChange} />
                  {setPerformanceMode && (
                    <SwitchRow
                      label={t('Performance mode')}
                      checked={performanceMode ?? false}
                      onChange={setPerformanceMode}
                      hint={t(PERFORMANCE_MODE_HINT)}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
          {openTab === 'filters' && (
            <div className={styles.nodePanelBody}>
              {hasFilterUi ? (
                <>
                  <div className={styles.nodePanelSection}>
                    <div className={styles.panelSettingsGroup}>
                      <SwitchRow
                        label={t('Dim instead of hide')}
                        checked={dimFilteredNodes}
                        onChange={onDimFilteredNodesChange}
                        hint={t(HIGHLIGHT_FILTER_HINT)}
                        guide={{ page: 'filtering', section: 'dim-instead-of-hide' }}
                      />
                    </div>
                  </div>
                  <FilterTabs active={filtersTab} onChange={setFiltersTab} />
                  <div
                    role="tabpanel"
                    id={`${FILTERS_TAB_ID}-panel-${filtersTab}`}
                    aria-labelledby={`${FILTERS_TAB_ID}-tab-${filtersTab}`}
                    className={styles.filterTabPanel}
                  >
                    {filtersTab === 'actors' &&
                      (hasActorFilters ? (
                        <>
                          {nodeFilterSections.map((section) => (
                            <FacetSection
                              key={section.key}
                              section={section}
                              selected={nodeFilters[section.key]}
                              onToggle={(value) => onToggleNodeFilter(section.key, value)}
                            />
                          ))}
                          {showMinZScoreSlider && (
                            <FilterSlider
                              id="socialGraphMinAvgZScore"
                              label={t('Minimum avg Z-Score')}
                              value={nodeFilters.minAvgZScore}
                              thresholds={MIN_AVG_ZSCORE_STEPS}
                              onChange={onMinAvgZScoreChange}
                            />
                          )}
                        </>
                      ) : (
                        <p className={styles.overviewNote}>{t('No actor filters for this graph.')}</p>
                      ))}
                    {filtersTab === 'connections' &&
                      (hasConnectionFilters ? (
                        <>
                          {mentionTypes.length > 1 && (
                            <div className={styles.nodePanelSection}>
                              <span className={styles.nodePanelSectionTitle}>{t('Interaction types')}</span>
                              <div className={styles.panelSettingsGroup}>
                                {mentionTypes.map(({ type, count }) => (
                                  <CheckboxRow
                                    key={type}
                                    label={`${mentionTypeLabel(t, type)} (${count})`}
                                    checked={selectedMentionTypes.includes(type)}
                                    onChange={() => onToggleMentionType(type)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {edgeAnomalyOptions.length > 1 && (
                            <div className={styles.nodePanelSection}>
                              <span className={styles.nodePanelSectionTitle}>{t('Relation anomaly')}</span>
                              <div className={styles.panelSettingsGroup}>
                                {edgeAnomalyOptions.map(({ bucket, count }) => (
                                  <CheckboxRow
                                    key={bucket}
                                    label={`${t(ZSCORE_BUCKET_LABELS[bucket])} (${count})`}
                                    checked={!hiddenEdgeAnomalyBuckets.includes(bucket)}
                                    onChange={() => onToggleEdgeAnomalyBucket(bucket)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {reciprocalCount > 0 && (
                            <div className={styles.nodePanelSection}>
                              <span className={styles.nodePanelSectionTitle}>{t('Connections')}</span>
                              <div className={styles.panelSettingsGroup}>
                                <CheckboxRow
                                  label={t('Mutual connections only')}
                                  checked={mutualOnly}
                                  onChange={() => onMutualOnlyChange(!mutualOnly)}
                                />
                              </div>
                            </div>
                          )}
                          {maxDegree > 0 && (
                            <FilterNumberInput
                              id="socialGraphMinDegree"
                              label={t('Minimum connections')}
                              value={nodeFilters.minDegree}
                              max={maxDegree}
                              matchCount={visibleActorCount}
                              onChange={onMinDegreeChange}
                            />
                          )}
                        </>
                      ) : (
                        <p className={styles.overviewNote}>{t('No connection filters for this graph.')}</p>
                      ))}
                  </div>
                </>
              ) : (
                <p className={styles.overviewNote}>{t('No filters available for this graph.')}</p>
              )}
            </div>
          )}
          {openTab === 'actors' && (
            <div className={styles.nodePanelBody}>
              <p className={styles.overviewNote}>
                {t('Select a category or a community to open it on the right and highlight its actors on the graph.')}
              </p>
              {actorsTabZscoreGroups.length > 0 && (
                <div className={styles.nodePanelSection}>
                  <span className={styles.nodePanelSectionTitle}>{t('Z-Score anomaly')}</span>
                  <div className={styles.actorGroupBox}>
                    {actorsTabZscoreGroups.map(({ bucket, members }) =>
                      renderGroupRow({
                        key: bucket,
                        inlineHint: true,
                        title: t(ZSCORE_BUCKET_LABELS[bucket]),
                        subtitle: t('{{count}} actors', { count: members.length }),
                        description: t(ZSCORE_BUCKET_DESCRIPTIONS[bucket]),
                        guide: { page: 'calculations' },
                        active: graphHighlight?.type === 'zscore' && graphHighlight.value === bucket,
                        onToggle: () =>
                          setGraphHighlight(
                            graphHighlight?.type === 'zscore' && graphHighlight.value === bucket
                              ? null
                              : { type: 'zscore', value: bucket },
                          ),
                        onAddAll: actions && (() => actions.addToGroup(members.map((member) => member.id))),
                      }),
                    )}
                  </div>
                </div>
              )}
              {(actorsTabRoleGroups.length > 0 || actorsTabFlagGroups.length > 0) && (
                <div className={styles.nodePanelSection}>
                  <span className={styles.nodePanelSectionTitle}>{t('Categories')}</span>
                  <div className={styles.actorGroupBox}>
                    {actorsTabRoleGroups.map(({ role, members }) =>
                      renderGroupRow({
                        key: `role:${role}`,
                        title: t(role),
                        subtitle: t('{{count}} actors', { count: members.length }),
                        description: ACTOR_ROLE_DESCRIPTIONS[role] && t(ACTOR_ROLE_DESCRIPTIONS[role]),
                        guide: { page: 'how-to-read', section: actorRoleSection(role, activeGraphKind) },
                        active: graphHighlight?.type === 'role' && graphHighlight.value === role,
                        onToggle: () =>
                          setGraphHighlight(
                            graphHighlight?.type === 'role' && graphHighlight.value === role
                              ? null
                              : { type: 'role', value: role },
                          ),
                        onAddAll: actions && (() => actions.addToGroup(members.map((member) => member.id))),
                      }),
                    )}
                    {actorsTabFlagGroups.map(({ flag, members }) =>
                      renderGroupRow({
                        key: `flag:${flag}`,
                        title: t(NODE_FLAG_LABELS[flag]),
                        subtitle: t('{{count}} actors', { count: members.length }),
                        description: t(NODE_FLAG_DESCRIPTIONS[flag]),
                        guide: nodeFlagSection(flag, activeGraphKind),
                        active: graphHighlight?.type === 'flag' && graphHighlight.value === flag,
                        onToggle: () =>
                          setGraphHighlight(
                            graphHighlight?.type === 'flag' && graphHighlight.value === flag
                              ? null
                              : { type: 'flag', value: flag },
                          ),
                        onAddAll: actions && (() => actions.addToGroup(members.map((member) => member.id))),
                      }),
                    )}
                  </div>
                </div>
              )}
              {actorsTabCommunities.length > 0 && (
                <div className={styles.nodePanelSection}>
                  <span
                    className={styles.nodePanelSectionTitleRow}
                    data-tooltip-id={GROUP_HINT_TOOLTIP_ID}
                    data-tooltip-content={t(COMMUNITIES_HINT)}
                    data-guide-page="how-to-read"
                    data-guide-section="communities"
                  >
                    <span className={styles.nodePanelSectionTitle}>{t('Communities')}</span>
                    <HintIcon className={styles.actorGroupHint} aria-hidden="true" />
                    <span className={styles.srOnly}>
                      {t(COMMUNITIES_HINT)}
                    </span>
                  </span>
                  <div className={styles.actorGroupBox}>
                    {actorsTabCommunities.map(({ cluster, members }) =>
                      renderGroupRow({
                        key: `community:${cluster.id}`,
                        title: t('Community #{{id}}', { id: cluster.id }),
                        // list rows stay one-line: count + the lead metric (homophily for
                        // mention graphs while density is hidden); the full metric set
                        // shows on the drill card
                        subtitle: [t('{{count}} actors', { count: members.length }), ...cluster.details.slice(0, 1)].join(
                          ' · ',
                        ),
                        active: graphHighlight?.type === 'community' && graphHighlight.value === cluster.id,
                        onToggle: () =>
                          setGraphHighlight(
                            graphHighlight?.type === 'community' && graphHighlight.value === cluster.id
                              ? null
                              : { type: 'community', value: cluster.id },
                          ),
                        // the whole community when nothing is hidden (keeps the
                        // community-tagged analytics event), otherwise the drawn actors
                        onAddAll:
                          actions &&
                          (() =>
                            hiddenNodeIds
                              ? actions.addToGroup(members.map((member) => member.id))
                              : actions.addClusterToGroup(cluster.id)),
                      }),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* shared hint bubble for group rows AND section-title ⓘ hints; opens
              to the right of the trigger, tail pointing back (Figma 683-1321) */}
          <GuideHintTooltip id={GROUP_HINT_TOOLTIP_ID} place="right" />

          {openTab === 'search' && (
            <div className={styles.nodePanelBody}>
              <form
                className={styles.panelSearchBox}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (searchMatches.length > 0) setFocusedNodeId(searchMatches[0].id);
                }}
              >
                <SearchIcon className={styles.panelSearchIcon} aria-hidden="true" />
                <label htmlFor="socialGraphSearch" className={styles.srOnly}>
                  {t('Search actor by name')}
                </label>
                <input
                  id="socialGraphSearch"
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('Search actors…')}
                  value={searchQuery}
                  onChange={(event) => onSearchQueryChange(event.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.panelSearchClear}
                    onClick={onClearSearch}
                    aria-label={t('Clear search')}
                  >
                    <CloseIcon aria-hidden="true" />
                  </button>
                )}
              </form>
              {searchLoading && (
                <div className={styles.panelSearchSkeleton} role="status" aria-live="polite">
                  <span className={styles.srOnly}>{t('Searching…')}</span>
                  {[0, 1, 2].map((row) => (
                    <div key={row} className={styles.panelSearchSkeletonRow} aria-hidden="true">
                      <span className={styles.panelSearchSkeletonName} />
                      <span className={styles.panelSearchSkeletonSub} />
                    </div>
                  ))}
                </div>
              )}
              {!searchLoading && searchMatches.length > 0 && (
                <ul className={styles.panelSearchResults}>
                  {searchMatches.map((match) => (
                    <li key={match.id}>
                      <button
                        type="button"
                        className={styles.panelSearchResult}
                        onClick={() => setFocusedNodeId(match.id)}
                      >
                        <span className={styles.panelSearchResultName}>{match.label}</span>
                        <span className={styles.panelSearchResultSub}>
                          <span
                            className={styles.panelSearchResultDot}
                            style={{
                              backgroundColor: COMMUNITY_PALETTE[match.community % COMMUNITY_PALETTE.length],
                            }}
                            aria-hidden="true"
                          />
                          {t('Community #{{id}}', { id: match.community })}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!searchLoading && searchQuery.trim().length >= 2 && searchMatches.length === 0 && (
                <p className={styles.overviewNote}>{t('No matching actors')}</p>
              )}
            </div>
          )}
        </aside>
      )}
    </>
  );
};
